'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { useNotification } from 'src/context/notification';
import { useLogging } from 'src/context/logging';
import { useAuthorization } from 'src/hooks/authorization';
import { Button, Checkbox, CircularProgress } from 'src/components';
import { EmptyState } from '../EmptyState';
import { PermissionMatrix } from 'src/components/Settings/Companies/components/RolesAdmin/components/PermissionMatrix';
import { RoleSummary } from 'src/components/Settings/Companies/components/RolesAdmin/components/RoleSummary';
import { ROLE_SECTION_COPY } from 'src/components/Settings/Companies/components/RolesAdmin/constants';
import {
  filterAssignableRoles,
  partitionRolesBySource,
} from 'src/components/Settings/Companies/components/RolesAdmin/utils';
import {
  useGetCompanyUserQuery,
  useUpdateCompanyUserMutation,
  useGetCompanyRolesQuery,
} from 'src/api/redux';

const UserRolesAdmin = () => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const { userManagementCapabilities } = useAuthorization();
  // MKT-429 opened role reads and assignment beyond MedScout staff: a License
  // Manager holds `read.company_roles.company` and `write.company_users.company`,
  // so assigning roles here is theirs to do. Editing role *definitions* stays
  // staff-only, and that lives in RolesAdmin rather than here.
  //
  // `isStaff` decides *how* the roles are described, not whether they can be
  // assigned: CS found the permission-code matrix unreadable, so a License
  // Manager gets a plain-language snapshot per role instead (MKT-527).
  const { canView, canWrite, isStaff } = userManagementCapabilities;

  // Get IDs from route params
  const { companyId, userId } = useParams<{
    companyId: string;
    userId: string;
  }>();

  const { data: currentUser, refetch: mutateUser } = useGetCompanyUserQuery(
    { companyId, userId },
    { skip: !companyId || !userId }
  );

  const [updateCompanyUser] = useUpdateCompanyUserMutation();

  const { data: roles, isLoading: isRolesLoading } = useGetCompanyRolesQuery(
    { companyId },
    { skip: !companyId || !canView }
  );

  const [isLoading, setIsLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  // Get current user's roles
  const currentUserRoles = useMemo(() => {
    if (!currentUser?.roles) return [];
    return currentUser.roles;
  }, [currentUser]);

  // Set initial selected roles
  useEffect(() => {
    if (currentUserRoles.length > 0) {
      setSelectedRoles(currentUserRoles);
    }
  }, [currentUserRoles]);

  // The roles this viewer may hand out. Rendering only — a staff-only role the
  // user already holds still has to survive a save, so `selectedRoles` and the
  // permission lookup below both stay on the unfiltered list (MKT-527).
  const assignableRoles = useMemo(
    () => filterAssignableRoles(roles?.results, isStaff),
    [roles?.results, isStaff]
  );

  // Combine permissions from all selected roles
  const combinedPermissions = useMemo(() => {
    if (!roles?.results || selectedRoles.length === 0) return [];
    return selectedRoles.reduce<string[]>((acc, roleCode) => {
      const role = roles.results.find((r) => r.code === roleCode);
      if (role) {
        return [...acc, ...role.permissions];
      }
      return acc;
    }, []);
  }, [roles, selectedRoles]);

  const handleCancel = () => {
    setSelectedRoles(currentUserRoles);
  };

  const handleSubmit = async () => {
    if (!companyId || !userId) return;

    const newRoles = new Set(selectedRoles);

    const data = {
      roles: Array.from(newRoles),
    };

    try {
      setIsLoading(true);
      // unwrap() so RTK Query throws on HTTP errors and the catch below runs
      // (otherwise a failed update would still show the success notification).
      await updateCompanyUser({
        companyId,
        userId,
        ...data,
      }).unwrap();

      setNotification({
        title: 'Success',
        message: 'User roles updated',
        type: 'success',
      });

      mutateUser();
    } catch (err) {
      log.exception(`Failed to update user roles`, err);
      setNotification({
        title: 'Error',
        // unwrap() throws the raw RTK Query error, so the API body is under `data`. Reading
        // `err.error_message` directly always missed, burying messages like "1 of the roles
        // provided are invalid." behind the generic fallback.
        message: err?.data?.error_message || 'Failed to update user roles',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = (roleCode: string, checked: boolean) => {
    if (checked) {
      setSelectedRoles((prev) =>
        prev.includes(roleCode) ? prev : [...prev, roleCode]
      );
    } else {
      setSelectedRoles((prev) => prev.filter((code) => code !== roleCode));
    }
  };

  /**
   * One assignable role: its checkbox, name and description. `withSummary` adds
   * the collapsible plain-language snapshot shown instead of the matrix.
   *
   * The summary sits *outside* the `<label>` on purpose: its "View details"
   * button would otherwise activate the label's control, so expanding a role
   * would assign it.
   */
  const renderRoleOption = (role: MedScout.Role, withSummary: boolean) => (
    <div key={role.code} className="flex flex-col gap-1">
      <label
        className={`flex gap-2 ${
          canWrite ? 'cursor-pointer' : 'cursor-default'
        }`}
      >
        {/* With a summary the block is tall enough that a centred checkbox
            floats away from the name it belongs to. */}
        <div
          className={
            withSummary ? 'flex items-start pt-0.5' : 'flex items-center'
          }
        >
          <Checkbox
            size="sm"
            data-testid={`role-checkbox-${role.code}`}
            name="role"
            value={role.code}
            checked={selectedRoles.includes(role.code)}
            onChange={(e) => handleRoleChange(role.code, e.target.checked)}
            disabled={!canWrite}
            className="h-4 w-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          {/* No per-role marker: which section a role sits under says whether
              it is the company's own or one of MedScout's (MKT-530). */}
          <div className="text-sm font-medium text-neutral-900">
            {role.label}
          </div>
          <div className="text-xs text-neutral-500">{role.description}</div>
        </div>
      </label>
      {withSummary && (
        // Indented to clear the checkbox (h-4 plus the gap-2 beside it) so the
        // disclosure lines up under the role name it belongs to.
        <RoleSummary
          permissions={role.permissions ?? []}
          roleLabel={role.label}
          className="pl-6"
          data-testid={`role-summary-${role.code}`}
        />
      )}
    </div>
  );

  /**
   * Roles split into MedScout's standard set and the company's own. A lone
   * "System roles" heading over every role would be noise, so the headings
   * only appear once there is actually a second group to tell apart.
   */
  const renderRoleGroups = (withSummary: boolean) => {
    const { standardRoles, customRoles, hasCustomRoles } =
      partitionRolesBySource(assignableRoles);

    if (!hasCustomRoles) {
      return assignableRoles.map((role) => renderRoleOption(role, withSummary));
    }

    const section = (
      key: string,
      heading: string,
      hint: string | undefined,
      roles: MedScout.Role[]
    ) =>
      roles.length ? (
        <div
          key={key}
          className="flex flex-col gap-3"
          data-testid={`role-section-${key}`}
        >
          <div className="flex flex-col gap-0.5 border-b border-neutral-200 pb-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              {heading}
            </span>
            {hint && (
              <span className="text-xs font-normal normal-case text-neutral-400">
                {hint}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-4">
            {roles.map((role) => renderRoleOption(role, withSummary))}
          </div>
        </div>
      ) : null;

    return [
      section('standard', ROLE_SECTION_COPY.standard, undefined, standardRoles),
      section(
        'custom',
        ROLE_SECTION_COPY.custom,
        ROLE_SECTION_COPY.customHint,
        customRoles
      ),
    ];
  };

  const renderContent = () => {
    if (isRolesLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <CircularProgress size="sm" color="neutral" />
        </div>
      );
    } else if (assignableRoles.length > 0) {
      // MedScout staff keep the matrix — they read permission codes for a living
      // and need the exact grant. Everyone else gets one wider column of roles,
      // each describing itself (MKT-527).
      if (!isStaff) {
        return (
          <div
            className="flex max-w-2xl flex-col gap-6"
            data-testid="role-list"
          >
            {renderRoleGroups(true)}
          </div>
        );
      }

      return (
        <div className="flex gap-6">
          {/* Role Details Column */}
          <div className="flex flex-col gap-6 w-64" data-testid="role-list">
            {renderRoleGroups(false)}
          </div>

          {/* Permissions Table */}
          <div
            className="flex flex-col flex-1 bg-white rounded-lg border border-neutral-200 overflow-hidden p-4"
            data-testid="permissions-table"
          >
            {selectedRoles.length > 0 && (
              <PermissionMatrix permissions={combinedPermissions} />
            )}
          </div>
        </div>
      );
    } else {
      return <EmptyState />;
    }
  };

  // Keyed off what the viewer can actually see, so the empty state and the
  // disabled Update button agree with the rendered list.
  const isEmpty = assignableRoles.length === 0;

  const isDisabled =
    JSON.stringify([...selectedRoles].sort()) ===
      JSON.stringify([...currentUserRoles].sort()) ||
    isLoading ||
    selectedRoles.length === 0 ||
    isEmpty;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center">
        <div className="text-lg font-semibold text-neutral-900">
          User Roles & Permissions
        </div>
      </div>

      {/* renderContent() covers the loading and empty cases */}
      {renderContent()}
      {canWrite && (
        <div className="flex justify-end gap-4">
          <Button
            variant="outlined"
            color="neutral"
            onClick={handleCancel}
            disabled={isDisabled}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            loading={isLoading}
            onClick={handleSubmit}
            variant="solid"
            color="primary"
            disabled={isDisabled}
            data-testid="update-button"
          >
            Update
          </Button>
        </div>
      )}
    </div>
  );
};

export default UserRolesAdmin;
